import type { TabsProps as AntTabsProps } from 'antd';
import React from 'react';
import { Badge } from '../Badge';
import { StyledTabs, TabLabelContent } from './styled';

export type TabsOrientation = 'horizontal' | 'vertical';

export interface TabsItem {
  key: string;
  label: React.ReactNode;
  children: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  badgeCount?: number | string;
}

export interface TabsProps
  extends Omit<AntTabsProps, 'items' | 'tabPosition' | 'className'> {
  items: TabsItem[];
  orientation?: TabsOrientation;
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (activeKey: string) => void;
  className?: string;
}

const orientationToTabPosition: Record<TabsOrientation, 'top' | 'left'> = {
  horizontal: 'top',
  vertical: 'left'
};

const renderTabLabel = ({
  icon,
  label,
  badgeCount
}: TabsItem): React.ReactNode => {
  if (!icon && badgeCount === undefined) {
    return label;
  }

  return (
    <TabLabelContent>
      {icon}
      <span>{label}</span>
      {badgeCount !== undefined && (
        <Badge variant="fillSecondary" size="small" count={badgeCount} />
      )}
    </TabLabelContent>
  );
};

export const Tabs: React.FC<TabsProps> = ({
  items,
  orientation = 'horizontal',
  className = '',
  ...props
}) => {
  const mappedItems = items.map((item) => ({
    key: item.key,
    disabled: item.disabled,
    label: renderTabLabel(item),
    children: item.children
  }));

  return (
    <StyledTabs
      className={className}
      items={mappedItems}
      tabPosition={orientationToTabPosition[orientation]}
      $orientation={orientation}
      {...props}
    />
  );
};
